/** ************************************************************************
 * Atomic Component for the bPerx iFrame UI.
 *
 * Atomic Type: Offer Display Product, Container of all Products
 * Purpose: Displays all products
 *
 * This component is a wrapper for all of the product components.
 * When the products are passed in as a list, this maps through the
 * list and generates individual product components.
 *
 * @file   Atomic Component, offer display, container for all products
 * @author Paul J Stales
 * @since  2.2.0
 ************************************************************************ */

import { FunctionalComponent, h } from 'preact'
import Product from '../product'
import { ProductMagazine } from '../../../../../../common/types/productTypes/ProductMagazine'
import { TransactionInformation } from '../../../../../../common/types/transactionInformationTypes/TransactionInformation'
import { css } from 'aphrodite'
import { IFRAME_CLASSNAMES } from '../../../../../../common/constants/ConstantsIFrameClassNames'
import { DisplayEvent } from '../../../../../../common/types/reportableEvents/subTypes/DisplayEvent'
import { ReportableEventType } from '../../../../../../common/types/reportableEvents/ReportableEvent'

/**
 * Props Interface for Products
 */
interface ProductsProps {
  styles: { [key: string]: any }
  allProducts: ProductMagazine[] | undefined
  transactionInformation: TransactionInformation
  setTransactionInformation: (
    transactionInformation: TransactionInformation
  ) => void
  setDoesPartnerNeedUpdateForTransactionInformation: (b: boolean) => void
  areProductsVisible: boolean
  setProductsVisible: (areProductsVisible: boolean) => void
  displayEvent: DisplayEvent
  setDisplayEvent: (displayEvent: DisplayEvent) => void
  displayEventHandler: (
    displayEvent: DisplayEvent,
    setDisplayEvent: (displayEvent: DisplayEvent) => void,
    newTransactionInformation: TransactionInformation,
    reportableEventType: string
  ) => void
}

/**
 * Maps over a list of products, creating a new <Product>
 * for every item in the list.
 *
 * React requires a unique key for list items; the unique
 * key here is merely their list index position.
 *
 * @param ProductsProps
 * @returns
 */
const Products: FunctionalComponent<ProductsProps> = ({
  styles,
  allProducts,
  transactionInformation,
  setTransactionInformation,
  setDoesPartnerNeedUpdateForTransactionInformation,
  areProductsVisible,
  setProductsVisible,
  displayEvent,
  setDisplayEvent,
  displayEventHandler
}) => {
  const productsList = allProducts?.map((product, i) => (
    <Product
      styles={styles}
      key={i}
      product={product}
      transactionInformation={transactionInformation}
      setTransactionInformation={setTransactionInformation}
      setDoesPartnerNeedUpdateForTransactionInformation={
        setDoesPartnerNeedUpdateForTransactionInformation
      }
      areProductsVisible={areProductsVisible}
      setProductsVisible={setProductsVisible}
      displayEvent={displayEvent}
      setDisplayEvent={setDisplayEvent}
      displayEventHandler={displayEventHandler}
    />
  ))

  return (
    <div
      class={`${IFRAME_CLASSNAMES.BPERX_PRODUCTS_LIST_CONTAINER}${' '}${
        styles[IFRAME_CLASSNAMES.BPERX_PRODUCTS_LIST_CONTAINER] === undefined
          ? ''
          : css(styles[IFRAME_CLASSNAMES.BPERX_PRODUCTS_LIST_CONTAINER])
      }`}
    >
      {productsList}
    </div>
  )
}

export default Products
